// SB6-ANA-FILT-05 — the cascade auto-clear (the load-bearing behavior).
//
// The faceted-counts endpoint returns, for the CURRENT selection, one
// `FacetAxis` per level with its options + per-option `disabled` flag
// (count 0 ⇒ disabled). A strict left-to-right cascade means: when a
// previously-selected option drops to count 0 (becomes disabled), it
// must be auto-CLEARED from the selection so the widgets don't filter
// on a dead criterion. Options that are disabled but NOT selected just
// render disabled (no state change).
//
// This is a pure function so it's trivially unit-testable; the effect
// that calls it lives in `useFilterCascade`.
import type {
  DataPointFacetsResponse,
  DataPointFilterAxis,
  DataPointFilterSelection,
} from "@aeriosiq/types";

// Map a FacetAxis.axis value → the selection key it controls.
const AXIS_TO_SELECTION: Record<
  DataPointFilterAxis,
  keyof DataPointFilterSelection
> = {
  status: "status",
  reviewState: "reviewState",
  kind: "kind",
  defectType: "defectType",
  component: "component",
  location: "location",
  severity: "severity",
};

/**
 * Given the current selection and a freshly-fetched facets response,
 * return the selection with every selected-but-now-disabled option
 * removed. Returns the SAME object reference when nothing changed so
 * callers can cheaply skip a no-op state write.
 */
export function clearDisabledSelections(
  selection: DataPointFilterSelection,
  facets: DataPointFacetsResponse,
): DataPointFilterSelection {
  let changed = false;
  const next: DataPointFilterSelection = { ...selection };
  const axes = Array.isArray(facets.axes) ? facets.axes : [];
  for (const axis of axes) {
    const selKey = AXIS_TO_SELECTION[axis.axis];
    // L1 Status is the cascade root; never auto-clear it (clearing the
    // last status would silently switch to the BE's ACTIVE-only default
    // mid-session, which is surprising). All lower levels auto-clear.
    if (selKey === "status") continue;
    const disabledKeys = new Set(
      axis.options.filter((o) => o.disabled).map((o) => o.key),
    );
    const current = selection[selKey];
    const filtered = current.filter((k) => !disabledKeys.has(k));
    if (filtered.length !== current.length) {
      next[selKey] = filtered;
      changed = true;
    }
  }
  return changed ? next : selection;
}
